import { Card, Grid, Typography, styled } from "@mui/material";
import React, { useRef } from "react";
import GtuLogo from "../gtu.png";
import ssrvm from "../ssrvm.png";
import { motion } from "framer-motion";
import gsap from "gsap";

const CustomCard = styled(Card)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 20px;
  width: 540px;
  gap: 50px;
  height: 340px;
  transition: box-shadow 0.4s ease;
  &:hover {
    box-shadow: 0 0 28px 2px #159895;
  }
`;

const headingVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

// ─────────────────────────────────────────────────────────────────────────────
// TiltCard — 3D flip entrance via Framer Motion + GSAP mouse tilt on hover
// ─────────────────────────────────────────────────────────────────────────────
const TiltCard = ({ children, delay }) => {
  const tiltRef = useRef(null);

  // Rotate toward cursor on hover
  const onMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    gsap.to(el, {
      rotateX: -((e.clientY - r.top) / r.height - 0.5) * 11,
      rotateY: ((e.clientX - r.left) / r.width - 0.5) * 14,
      transformPerspective: 750,
      duration: 0.35,
      ease: 'power2.out',
    });
  };

  // Spring back to flat on leave
  const onLeave = () => {
    gsap.to(tiltRef.current, {
      rotateX: 0,
      rotateY: 0,
      duration: 0.65,
      ease: 'elastic.out(1, 0.5)',
    });
  };

  return (
    // Outer div provides the CSS perspective context for the flip entrance
    <div style={{ perspective: '1000px' }}>
      {/* Framer Motion owns the entrance — flips from rotateX 72° to 0° */}
      <motion.div
        initial={{ opacity: 0, rotateX: 72, y: 44 }}
        whileInView={{ opacity: 1, rotateX: 0, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Inner div — GSAP owns the hover tilt (separate from entrance transform) */}
        <div
          ref={tiltRef}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          style={{ willChange: 'transform', display: 'inline-block' }}
        >
          {children}
        </div>
      </motion.div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Education = () => {
  const companyData = [
    {
      name: "Gujarat Technological University",
      logo: GtuLogo,
      degree: "Bachelor's of Engineering",
      duration: "3 Years",
      marks: "8.5 CGPA",
    },
    {
      name: "Gujarat Technological University",
      logo: GtuLogo,
      degree: "Diploma Engineering",
      duration: "3 Years",
      marks: "7 CGPA",
    },
    {
      name: "Central Board of Secondary Education",
      logo: ssrvm,
      degree: "Secondary School Certificate",
      duration: "10 Years",
      marks: "6.2 CGPA",
    },
  ];

  return (
    <Grid
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      display="flex"
      py={2}
      container
      component="div"
      id="education"
    >
      {/* Section heading */}
      <motion.div
        variants={headingVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.5 }}
        style={{ width: '100%' }}
      >
        <Grid
          item
          style={{ borderRadius: '0px 40px 0px 40px' }}
          mt={10}
          mb={5}
          alignItems="center"
          justifyContent="center"
          xs={12}
          display="flex"
          border="solid 1px"
        >
          <Typography fontFamily="Alkatra" fontSize={30} fontWeight={700}>
            Education
          </Typography>
        </Grid>
      </motion.div>

      {/* Cards — flip entrance + interactive hover tilt */}
      <Grid
        item
        xs={12}
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        justifyContent="center"
      >
        {companyData.map((item, index) => (
          <TiltCard key={index} delay={index * 0.18}>
            <CustomCard>
              <img alt={item.name} src={item.logo} height="auto" width="auto" />
              <Grid
                container
                style={{ display: 'flex', alignItems: 'center', padding: '20px' }}
                sx={{ justifyContent: { xs: 'center', md: 'space-between' } }}
              >
                <Grid item sx={{ display: { xs: 'none', md: 'unset' } }}>
                  <Typography align="left" fontSize={18} fontWeight={500}>Institution Name</Typography>
                  <Typography align="left" fontSize={18} fontWeight={500}>Degree</Typography>
                  <Typography align="left" fontSize={18} fontWeight={500}>Marks</Typography>
                  <Typography align="left" fontSize={18} fontWeight={500}>Duration</Typography>
                </Grid>
                <Grid item>
                  <Typography align="center" fontWeight={600}>{item.name}</Typography>
                  <Typography align="center" fontWeight={600}>{item.degree}</Typography>
                  <Typography align="center" fontWeight={600}>{item.marks}</Typography>
                  <Typography align="center" fontWeight={600}>{item.duration}</Typography>
                </Grid>
              </Grid>
            </CustomCard>
          </TiltCard>
        ))}
      </Grid>
    </Grid>
  );
};

export default Education;
